import { Check } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';

interface SaveButtonProps extends ButtonProps {
  /** True while the underlying mutation is in flight. */
  pending: boolean;
  /** Becomes true once after a successful mutation. The button will display a
   *  `<Check />` for `successDurationMs` (default 1200ms) before reverting. */
  success: boolean;
  /** Optional callback fired when the success display window closes. Useful if
   *  the parent wants to reset its own `success` flag. */
  onSuccessFade?: () => void;
  successDurationMs?: number;
  /** Label shown while idle. */
  children: React.ReactNode;
  /** Label shown while pending. */
  pendingLabel?: React.ReactNode;
  /** Label shown alongside the check during the success window. */
  successLabel?: React.ReactNode;
}

/**
 * Save / submit button that flashes a green check for ~1.2s after a
 * successful mutation. The sonner toast still fires from the page; this gives
 * the form itself a small acknowledgment so the success isn't entirely
 * outside the user's focus.
 */
export const SaveButton = forwardRef<HTMLButtonElement, SaveButtonProps>(
  function SaveButton(
    {
      pending,
      success,
      onSuccessFade,
      successDurationMs = 1200,
      children,
      pendingLabel = 'Saving…',
      successLabel = 'Saved',
      disabled,
      ...rest
    },
    ref,
  ) {
    const [showSuccess, setShowSuccess] = useState(false);
    const onSuccessFadeRef = useRef(onSuccessFade);
    onSuccessFadeRef.current = onSuccessFade;

    useEffect(() => {
      if (!success) return;
      setShowSuccess(true);
      const t = window.setTimeout(() => {
        setShowSuccess(false);
        onSuccessFadeRef.current?.();
      }, successDurationMs);
      return () => window.clearTimeout(t);
    }, [success, successDurationMs]);

    return (
      <Button ref={ref} disabled={disabled || pending} {...rest}>
        {pending ? (
          pendingLabel
        ) : showSuccess ? (
          <>
            <Check className="h-4 w-4 motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-150" />
            {successLabel}
          </>
        ) : (
          children
        )}
      </Button>
    );
  },
);
